import { makeAutoObservable, runInAction } from 'mobx';
import { Device, Message, FileInfo, apiClient } from '../services/api';
import { localStorageService } from '../services/LocalStorageService';
import { notificationService } from '../services/NotificationService';
import { secureStorage } from '../services/SecureStorageService';
import API_CONFIG from '../config/api.config';

export interface ToastState {
  show: boolean;
  message: string;
}

export class AppStore {
  // Devices
  devices: Device[] = [];
  selectedDevice: Device | null = null;
  isLoadingDevices = false;

  // Messages
  messages: Message[] = [];
  isLoadingMessages = false;

  // UI State
  isLoading = true;
  error: string | null = null;
  serverConnected = false;
  isSending = false;

  // Message Input
  messageText = '';
  selectedImage: File | null = null; // For backward compatibility
  imagePreview: string | null = null; // Preview URL for single image (backward compatibility)
  selectedFiles: File[] = []; // Array of files for multiple file support
  filePreviews: Map<number, string> = new Map(); // Preview URLs for files
  
  // Session Management
  currentSessionId: string | null = null; // Current upload session ID
  sessionFiles: FileInfo[] = []; // Files in current session
  sessionTotalSize: number = 0; // Total size of files in session
  
  // Upload Progress Tracking
  fileUploadStatus: Map<number, 'uploading' | 'uploaded' | 'error'> = new Map(); // Track upload status per file index
  isUploadingFiles = false; // Global flag for file uploads in progress

  // External Message State
  currentExternalSessionId: string | null = null; // Current external message session ID
  externalMessageCode: string | null = null; // 6-digit code for external messages
  isCreatingExternalMessage = false; // Flag for creating external messages
  externalMessageError: string | null = null; // Error message for external messages
  externalMessageSuccess = false; // Success flag for external messages

  // Toast
  toast: ToastState = { show: false, message: '' };

  // Message Deletion Timer Dialog
  showDeletionTimerDialog = false;

  // Storage Info
  storageInfo = { messageCount: 0, estimatedSize: '0 Bytes' };

  // New Message Notification
  hasNewMessages = false;

  constructor() {
    makeAutoObservable(this);
    this.initializeLocalStorage();
  }

  private async initializeLocalStorage() {
    try {
      await localStorageService.init();
      // Cleanup old messages on startup
      await localStorageService.cleanupOldMessages();
      // Update storage info
      await this.updateStorageInfo();
    } catch (err) {
      console.error('Failed to initialize local storage:', err);
    }
  }

  private async updateStorageInfo() {
    try {
      const info = await localStorageService.getStorageSize();
      runInAction(() => {
        this.storageInfo = info;
      });
    } catch (err) {
      console.error('Failed to update storage info:', err);
    }
  }

  // Device Management
  async loadDevices() {
    this.isLoadingDevices = true;
    try {
      const devices = await apiClient.getDevices();
      runInAction(() => {
        this.devices = devices;
        this.isLoadingDevices = false;
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || 'Failed to load devices';
        this.isLoadingDevices = false;
      });
    }
  }

  setSelectedDevice(device: Device | null) {
    // Clear session when device changes
    if (this.selectedDevice?.id !== device?.id) {
      this.clearSession();
      this.clearFileInput();
    }
    
    this.selectedDevice = device;
    if (device) {
      this.loadMessages(device.id);
    } else {
      this.messages = [];
    }
  }

  async renameDevice(deviceId: string, newName: string) {
    try {
      const updatedDevice = await apiClient.updateDevice(deviceId, newName);
      runInAction(() => {
        this.devices = this.devices.map(device => 
          device.id === deviceId ? updatedDevice : device
        );
        if (this.selectedDevice?.id === deviceId) {
          this.selectedDevice = updatedDevice;
        }
        this.error = null; // Clear any previous errors
      });
      this.showToast('Device renamed successfully');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to rename device';
      runInAction(() => {
        this.error = errorMessage;
      });
      // Show detailed error message in toast
      this.showToast(errorMessage);
    }
  }

  async deleteCurrentDevice(deviceId: string, onSuccess: () => void) {
    try {
      // 1. Delete device from server
      await apiClient.deleteDevice(deviceId);
      
      // 2. Clear local storage (messages cache)
      await localStorageService.clearAllMessages();
      
      // 3. Clear tokens and device ID
      secureStorage.clearAll();
      
      // 4. Clear API client token
      apiClient.setAccessToken(null);
      
      // 5. Clear store state
      this.clearAllData();
      
      // 6. Call success callback (navigate to login)
      this.showToast('Device deleted successfully');
      setTimeout(() => {
        onSuccess();
      }, 500);
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || 'Failed to delete device';
      });
      throw err;
    }
  }

  // Clear all data from store (used for logout)
  clearAllData() {
    runInAction(() => {
      // Clear devices
      this.devices = [];
      this.selectedDevice = null;
      
      // Clear messages
      this.messages = [];
      
      // Clear input state
      this.messageText = '';
      this.selectedImage = null;
      this.imagePreview = null;
      this.selectedFiles = [];
      this.filePreviews.clear();
      
      // Clear session state
      this.currentSessionId = null;
      this.sessionFiles = [];
      this.sessionTotalSize = 0;
      this.fileUploadStatus.clear();
      this.isUploadingFiles = false;
      
      // Clear external message state
      this.currentExternalSessionId = null;
      this.externalMessageCode = null;
      this.isCreatingExternalMessage = false;
      this.externalMessageError = null;
      this.externalMessageSuccess = false;
      
      // Clear UI state
      this.error = null;
      this.isSending = false;
      this.isLoadingDevices = false;
      this.isLoadingMessages = false;
      this.hasNewMessages = false;
      
      // Clear storage info
      this.storageInfo = { messageCount: 0, estimatedSize: '0 Bytes' };
    });
  }

  async deleteOtherDevice(deviceId: string) {
    try {
      // 1. Delete device from server
      await apiClient.deleteDevice(deviceId);
      
      // 2. Clear local storage (messages cache) for this specific device
      await localStorageService.clearMessages(deviceId);
      
      // 3. Remove device from store
      runInAction(() => {
        this.devices = this.devices.filter(d => d.id !== deviceId);
        // If the selected device was deleted, clear selection
        if (this.selectedDevice?.id === deviceId) {
          this.selectedDevice = null;
          this.messages = [];
        }
      });
      
      // 4. Reload devices list to ensure consistency
      await this.loadDevices();
      
      // 5. Show success message
      this.showToast('Device deleted successfully');
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || 'Failed to delete device';
      });
      throw err;
    }
  }

  // Message Management
  async loadMessages(deviceId: string, showLoadingIndicator: boolean = true) {
    if (showLoadingIndicator) {
      this.isLoadingMessages = true;
    }
    
    try {
      const currentDeviceId = secureStorage.getDeviceId();
      console.log('Loading messages for device:', deviceId, 'Current device:', currentDeviceId, 'Is current device:', deviceId === currentDeviceId);
      
      if (currentDeviceId && deviceId === currentDeviceId) {
        // For current device: load from local storage first, then check server for new messages
        console.log('Using local storage for current device');
        
        // Load existing messages from local storage immediately (no blinking)
        // But only update UI if we're currently viewing this device
        try {
          await localStorageService.init();
          const localMessages = await localStorageService.getMessages(deviceId);
          runInAction(() => {
            // Only update UI if we're viewing this device
            if (this.selectedDevice?.id === deviceId) {
              this.messages = localMessages;
            }
            this.isLoadingMessages = false;
          });
        } catch (err) {
          console.error('Failed to load from local storage:', err);
        }
        
        // Then check for new messages from server (without showing loading indicator)
        await this.loadCurrentDeviceMessages(deviceId);
      } else {
        // For other devices: just load from server (no local storage)
        // Filter out read messages - only show unread messages for other devices
        console.log('Using server only for other device');
        const allMessages = await apiClient.getDeviceContent(deviceId, false);
        const unreadMessages = allMessages.filter(msg => !msg.isRead);
        runInAction(() => {
          this.messages = unreadMessages;
          this.isLoadingMessages = false;
        });
      }
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || 'Failed to load messages';
        this.isLoadingMessages = false;
      });
    }
  }

  private async loadCurrentDeviceMessages(deviceId: string) {
    try {
      // Initialize local storage service
      await localStorageService.init();
      
      // Get messages from server
      const serverMessages = await apiClient.getDeviceContent(deviceId, true); // deleteAfterRead=true
      
      if (serverMessages.length > 0) {
        console.log('Received messages from server:', serverMessages);
        
        // Log image URLs for debugging
        serverMessages.forEach(msg => {
          if (msg.type === 'image' && msg.imageUrl) {
            console.log('Image message URL:', msg.imageUrl, 'for message:', msg.contentId);
          }
        });
        
        // Store new messages locally (including downloading images)
        await localStorageService.storeMessages(deviceId, serverMessages);
        this.showToast(`${serverMessages.length} new message(s) received and stored locally`);
        
        // Mark image and files messages for deletion after storing locally
        const imageAndFilesMessages = serverMessages.filter(
          msg => (msg.type === 'image' || msg.type === 'files') && msg.id
        );
        
        // Call mark-delete endpoint for each image/files message
        for (const msg of imageAndFilesMessages) {
          if (msg.id) {
            try {
              await apiClient.markMessageForDeletion(msg.id);
              console.log(`Message ${msg.id} (${msg.type}) marked for deletion`);
            } catch (err: any) {
              console.error(`Failed to mark message ${msg.id} for deletion:`, err);
              // Continue with other messages even if one fails
            }
          }
        }
        
        // Show browser notification for new messages
        serverMessages.forEach(msg => {
          const fromDevice = msg.fromDeviceName || msg.fromDeviceId || 'Unknown device';
          if (msg.type === 'text') {
            notificationService.showMessageNotification(fromDevice, 'text', msg.text);
          } else if (msg.type === 'image' || msg.type === 'files') {
            notificationService.showMessageNotification(fromDevice, msg.type === 'files' ? 'files' : 'image');
          }
        });

        // Trigger new message highlight animation
        runInAction(() => {
          this.hasNewMessages = true;
        });
        
        // Auto-clear highlight after 3 seconds
        setTimeout(() => {
          runInAction(() => {
            this.hasNewMessages = false;
          });
        }, 3000);
        
        // Update storage info after storing new messages
        await this.updateStorageInfo();
        
        // Load all messages from local storage
        const localMessages = await localStorageService.getMessages(deviceId);
        
        // Only update UI messages if we're currently viewing this device
        runInAction(() => {
          if (this.selectedDevice?.id === deviceId) {
            // Merge new messages with existing ones (avoid duplicates)
            const existingIds = new Set(this.messages.map(m => m.contentId));
            const newMessages = localMessages.filter(m => !existingIds.has(m.contentId));
            
            if (newMessages.length > 0) {
              // Prepend new messages and sort by timestamp (newest first)
              this.messages = [...localMessages].sort((a, b) => b.timestamp - a.timestamp);
            }
          }
          
          this.isLoadingMessages = false;
        });
      } else {
        // No new messages, just update loading state
        runInAction(() => {
          this.isLoadingMessages = false;
        });
      }
    } catch (err: any) {
      // If local storage fails, try to load from server as fallback
      console.error('Local storage error, falling back to server:', err);
      const messages = await apiClient.getDeviceContent(deviceId, false);
      runInAction(() => {
        this.messages = messages;
        this.isLoadingMessages = false;
      });
    }
  }


  // Remove files from session
  async removeFilesFromSession(fileUrls: string[]) {
    if (!this.currentSessionId || fileUrls.length === 0) {
      return;
    }

    try {
      const response = await apiClient.removeFilesFromSession(this.currentSessionId, fileUrls);
      
      runInAction(() => {
        this.sessionFiles = response.files || [];
        // Calculate total size from the returned files array
        this.sessionTotalSize = (response.files || []).reduce((sum: number, file: FileInfo) => sum + (file.size || 0), 0);
      });
    } catch (err: any) {
      console.error('Failed to remove files from session:', err);
      throw err;
    }
  }

  // Finalize session (send message)
  async finalizeSession() {
    if (!this.currentSessionId || !this.selectedDevice) {
      throw new Error('No active session to finalize');
    }

    this.isSending = true;
    this.error = null;

    try {
      await apiClient.finalizeSession(this.currentSessionId);
      
      const filesCount = this.sessionFiles.length;
      this.clearSession();
      this.clearFileInput();
      this.showToast(`${filesCount} file(s) sent`);
      this.showDeletionTimer();
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || 'Failed to send message';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isSending = false;
      });
    }
  }

  // Clear current session
  clearSession() {
    this.currentSessionId = null;
    this.sessionFiles = [];
    this.sessionTotalSize = 0;
    this.fileUploadStatus.clear();
    this.isUploadingFiles = false;
  }

  // External Message Methods
  async createExternalMessageSession(messageText?: string, files?: File[]) {
    runInAction(() => {
      this.isCreatingExternalMessage = true;
      this.externalMessageError = null;
      this.externalMessageSuccess = false;
    });

    try {
      if (messageText && messageText.trim()) {
        // Create text message session
        const response = await apiClient.createExternalMessageSession('text', messageText);
        
        runInAction(() => {
          this.currentExternalSessionId = response.sessionId;
          this.externalMessageCode = response.code || null;
          this.isCreatingExternalMessage = false;
          this.externalMessageSuccess = true;
        });
        
        this.showToast('External message code generated');
        this.showDeletionTimer();
      } else if (files && files.length > 0) {
        // Convert files to FileData format
        const fileDataPromises = files.map(async (file) => {
          const base64 = await this.fileToBase64(file);
          return {
            data: base64.split(',')[1], // Remove data URL prefix
            type: file.type || 'application/octet-stream',
            extension: this.getFileExtension(file.name),
            name: file.name
          };
        });
        
        const fileDataArray = await Promise.all(fileDataPromises);
        
        // Create files session
        const response = await apiClient.createExternalMessageSession('files', undefined, fileDataArray);
        
        runInAction(() => {
          this.currentExternalSessionId = response.sessionId;
          this.isCreatingExternalMessage = false;
        });
      } else {
        throw new Error('No content provided');
      }
    } catch (err: any) {
      runInAction(() => {
        this.isCreatingExternalMessage = false;
        this.externalMessageError = err.message || 'Failed to create external message session';
      });
      throw err;
    }
  }

  async addFilesToExternalSession(files: File[]) {
    if (!this.currentExternalSessionId) {
      throw new Error('No active external session');
    }

    runInAction(() => {
      this.isUploadingFiles = true;
      this.externalMessageError = null;
    });

    try {
      // Convert files to FileData format
      const fileDataPromises = files.map(async (file) => {
        const base64 = await this.fileToBase64(file);
        return {
          data: base64.split(',')[1], // Remove data URL prefix
          type: file.type || 'application/octet-stream',
          extension: this.getFileExtension(file.name),
          name: file.name
        };
      });
      
      const fileDataArray = await Promise.all(fileDataPromises);
      
      const response = await apiClient.addFilesToExternalSession(this.currentExternalSessionId, fileDataArray);
      
      runInAction(() => {
        this.isUploadingFiles = false;
      });
      
      return response;
    } catch (err: any) {
      runInAction(() => {
        this.isUploadingFiles = false;
        this.externalMessageError = err.message || 'Failed to add files to external session';
      });
      throw err;
    }
  }

  async finalizeExternalSession() {
    if (!this.currentExternalSessionId) {
      throw new Error('No active external session');
    }

    runInAction(() => {
      this.isCreatingExternalMessage = true;
      this.externalMessageError = null;
    });

    try {
      const response = await apiClient.finalizeExternalSession(this.currentExternalSessionId);
      
      runInAction(() => {
        this.externalMessageCode = response.code;
        this.isCreatingExternalMessage = false;
        this.externalMessageSuccess = true;
      });
      
      this.showToast('External message code generated');
      this.showDeletionTimer();
    } catch (err: any) {
      runInAction(() => {
        this.isCreatingExternalMessage = false;
        this.externalMessageError = err.message || 'Failed to finalize external session';
      });
      throw err;
    }
  }

  async removeFileFromExternalSession(fileUrl: string) {
    if (!this.currentExternalSessionId) {
      throw new Error('No active external session');
    }

    try {
      const response = await apiClient.removeFilesFromExternalSession(
        this.currentExternalSessionId,
        [fileUrl]
      );
      
      runInAction(() => {
        // Update session files if needed
      });
      
      return response;
    } catch (err: any) {
      runInAction(() => {
        this.externalMessageError = err.message || 'Failed to remove file from external session';
      });
      throw err;
    }
  }

  async receiveExternalMessageByCode(code: string) {
    runInAction(() => {
      this.isCreatingExternalMessage = true;
      this.externalMessageError = null;
    });

    try {
      const response = await apiClient.receiveExternalMessage(code);
      
      // Validate that we have a valid ID
      if (!response.id || typeof response.id !== 'string') {
        throw new Error('Invalid message ID received from server');
      }
      
      // Convert received message to Message format and save to local storage
      const message: Message = {
        contentId: response.id, // Use 'id' from response
        type: response.type as 'text' | 'image' | 'files',
        text: response.text,
        files: response.files,
        timestamp: response.timestamp || Date.now(),
        fromDeviceId: response.fromDeviceId,
        fromDeviceName: response.fromDeviceName || 'External',
        fromUserName: response.fromUserName,
        isDelivered: true,
        isRead: false
      };

      // Save to local storage
      if (this.currentDevice) {
        await localStorageService.saveMessage(this.currentDevice.id, message);
      }

      // Delete external message from server after successful download
      try {
        await apiClient.deleteExternalMessage(response.id);
      } catch (deleteErr) {
        console.error('Failed to delete external message from server:', deleteErr);
      }

      // Reload messages
      if (this.currentDevice) {
        await this.loadMessages(this.currentDevice.id);
      }

      runInAction(() => {
        this.isCreatingExternalMessage = false;
        this.externalMessageSuccess = true;
      });

      this.showToast('Message received successfully');
    } catch (err: any) {
      runInAction(() => {
        this.isCreatingExternalMessage = false;
        this.externalMessageError = err.message || 'Failed to receive external message';
      });
      throw err;
    }
  }

  clearExternalSession() {
    runInAction(() => {
      this.currentExternalSessionId = null;
      this.externalMessageCode = null;
      this.externalMessageError = null;
      this.externalMessageSuccess = false;
      this.isCreatingExternalMessage = false;
    });
  }

  // Helper methods
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private getFileExtension(filename: string): string {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : 'bin';
  }

  async sendMessage() {
    const hasFiles = this.selectedFiles.length > 0 || this.selectedImage;
    const hasText = this.messageText.trim();
    
    if ((!hasText && !hasFiles) || !this.selectedDevice || this.isSending || this.isUploadingFiles) {
      return;
    }

    // If files are still uploading, don't allow sending
    if (this.isUploadingFiles) {
      this.setError('Please wait for files to finish uploading');
      return;
    }

    this.isSending = true;
    this.error = null;

    try {
      // Handle multiple files - finalize session
      if (this.selectedFiles.length > 0) {
        // Check if all files are uploaded
        const allUploaded = Array.from(this.fileUploadStatus.values()).every(
          status => status === 'uploaded'
        );
        
        if (!allUploaded) {
          throw new Error('Some files failed to upload. Please remove them and try again.');
        }
        
        // Finalize the session to send
        if (this.currentSessionId) {
          await this.finalizeSession();
        } else {
          throw new Error('No active session to send');
        }
      } else if (this.selectedImage) {
        // Send single image immediately (backward compatibility)
        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(this.selectedImage!);
        });

        await apiClient.sendContent({
          type: 'image',
          imageData: base64Data,
          targetDeviceId: this.selectedDevice.id
        });

        this.clearImageInput();
        this.showToast('Image sent');
        this.showDeletionTimer();
      } else if (hasText) {
        // Send text message immediately
        await apiClient.sendContent({
          type: 'text',
          text: this.messageText.trim(),
          targetDeviceId: this.selectedDevice.id
        });

        this.clearTextInput();
        this.showToast('Message sent');
        this.showDeletionTimer();
      }
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || 'Failed to send message';
      });
    } finally {
      runInAction(() => {
        this.isSending = false;
      });
    }
  }

  async forwardMessage(message: Message, targetDevice: Device) {
    runInAction(() => {
      this.isSending = true;
      this.error = null;
    });

    try {
      // Handle multiple files
      if (message.type === 'files' && message.files && message.files.length > 0) {
        // Convert files to base64 - files should already be in local storage as data URLs
        const fileDataPromises = message.files.map(async (file) => {
          let base64Data: string;
          
          // Handle base64 data URLs (from local storage)
          if (file.url.startsWith('data:')) {
            // Extract base64 part from data URL
            const base64Part = file.url.includes(',') ? file.url.split(',')[1] : file.url;
            base64Data = base64Part;
          } else {
            // Fallback: try to fetch from server (should rarely happen if files are stored locally)
            console.warn('File not found in local storage, attempting to fetch from server:', file.url);
            try {
              const { getFullUrl } = await import('../config/api.config');
              const fullUrl = getFullUrl(file.url);
              const response = await fetch(fullUrl);
              const blob = await response.blob();
              
              base64Data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const result = reader.result as string;
                  const base64 = result.includes(',') ? result.split(',')[1] : result;
                  resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } catch (err) {
              throw new Error(`Failed to fetch file ${file.name || file.url}: ${err}`);
            }
          }

          return {
            data: base64Data,
            type: file.type || 'application/octet-stream',
            extension: file.extension || 'jpg',
            name: file.name || `file.${file.extension || 'jpg'}`
          };
        });

        const filesData = await Promise.all(fileDataPromises);

        // Create session with files
        const sessionResponse = await apiClient.sendContent({
          type: 'files',
          files: filesData,
          targetDeviceId: targetDevice.id
        });

        // Finalize the session to actually send the message
        if (sessionResponse && sessionResponse.sessionId) {
          await apiClient.finalizeSession(sessionResponse.sessionId);
          this.showToast(`${message.files.length} file(s) forwarded to ${targetDevice.name}`);
          this.showDeletionTimer();
        } else {
          throw new Error('Failed to create session for forwarding files');
        }
      } else if (message.type === 'image' && message.imageUrl) {
        // For single image messages (backward compatibility)
        // Image should already be in local storage as data URL
        let imageData: string;
        
        if (message.imageUrl.startsWith('data:')) {
          // Extract base64 part from data URL
          // Data URL format: data:[<mediatype>][;base64],<data>
          const commaIndex = message.imageUrl.indexOf(',');
          if (commaIndex === -1 || commaIndex === message.imageUrl.length - 1) {
            throw new Error(`Invalid data URL format for image: missing data`);
          }
          imageData = message.imageUrl.substring(commaIndex + 1);
          if (!imageData || imageData.trim() === '') {
            throw new Error('Empty base64 data for image');
          }
        } else {
          // Fallback: try to fetch from server (should rarely happen if images are stored locally)
          console.warn('Image not found in local storage, attempting to fetch from server:', message.imageUrl);
          try {
            const { getFullUrl } = await import('../config/api.config');
            const fullUrl = getFullUrl(message.imageUrl);
            const response = await fetch(fullUrl);
            const blob = await response.blob();
            
            imageData = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                // Extract base64 part from data URL
                const commaIndex = result.indexOf(',');
                if (commaIndex === -1 || commaIndex === result.length - 1) {
                  reject(new Error('Invalid data URL format from FileReader: missing data'));
                  return;
                }
                resolve(result.substring(commaIndex + 1));
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } catch (err) {
            throw new Error(`Failed to fetch image: ${err}`);
          }
        }

        // For images, we need to use the files session flow as well
        // Create a session with the image as a single file
        const imageFileData = {
          data: imageData,
          type: 'image/jpeg', // Default type, could be improved by detecting from data URL
          extension: 'jpg',
          name: 'image.jpg'
        };

        const sessionResponse = await apiClient.sendContent({
          type: 'files',
          files: [imageFileData],
          targetDeviceId: targetDevice.id
        });

        // Finalize the session to actually send the message
        if (sessionResponse && sessionResponse.sessionId) {
          await apiClient.finalizeSession(sessionResponse.sessionId);
          this.showToast(`Image forwarded to ${targetDevice.name}`);
          this.showDeletionTimer();
        } else {
          throw new Error('Failed to create session for forwarding image');
        }
      } else if (message.type === 'text' && message.text) {
        // For text messages, forward the text
        await apiClient.sendContent({
          type: 'text',
          text: message.text,
          targetDeviceId: targetDevice.id
        });
        this.showToast(`Message forwarded to ${targetDevice.name}`);
        this.showDeletionTimer();
      } else {
        throw new Error('Unsupported message type for forwarding');
      }
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || 'Failed to forward message';
      });
      // Re-throw error so caller can handle it
      throw err;
    } finally {
      runInAction(() => {
        this.isSending = false;
      });
    }
  }

  async deleteMessage(messageId: string) {
    try {
      const currentDeviceId = secureStorage.getDeviceId();
      
      if (currentDeviceId && this.selectedDevice?.id === currentDeviceId) {
        // For current device: delete from local storage
        await localStorageService.deleteMessage(messageId);
        
        // Also try to delete from server (in case it still exists)
        try {
          await apiClient.deleteMessage(messageId);
        } catch (err) {
          // Ignore server deletion errors for local messages
          console.log('Message already deleted from server or server error:', err);
        }
      } else {
        // For other devices: delete from server
        await apiClient.deleteMessage(messageId);
      }
      
      // Update local state
      runInAction(() => {
        this.messages = this.messages.filter(msg => msg.contentId !== messageId);
      });
      
      // Update storage info after deletion
      await this.updateStorageInfo();
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || 'Failed to delete message';
      });
    }
  }

  async clearAllMessages() {
    try {
      const currentDeviceId = secureStorage.getDeviceId();
      
      if (currentDeviceId && this.selectedDevice?.id === currentDeviceId) {
        // For current device: clear all from local storage
        await localStorageService.clearMessages(currentDeviceId);
        
        // Update local state
        runInAction(() => {
          this.messages = [];
        });
        
        // Update storage info after clearing
        await this.updateStorageInfo();
        
        this.showToast('All messages cleared');
      }
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || 'Failed to clear messages';
      });
    }
  }

  // Server Connection
  async checkServerConnection() {
    try {
      await apiClient.healthCheck();
      runInAction(() => {
        this.serverConnected = true;
        // Clear error when server connection is restored
        if (this.error && (this.error.includes('Network Error') || this.error.includes('Failed to'))) {
          this.error = null;
        }
      });
    } catch (err) {
      runInAction(() => {
        this.serverConnected = false;
      });
    }
  }

  // Input Management
  setMessageText(text: string) {
    this.messageText = text;
    if (text.trim() && (this.selectedImage || this.selectedFiles.length > 0)) {
      // Clear files if text is entered
      this.clearFileInput();
    }
  }

  setSelectedImage(file: File | null) {
    this.selectedImage = file;
    if (file && this.messageText.trim()) {
      // Clear text if image is selected
      this.clearTextInput();
    }
    
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        runInAction(() => {
          this.imagePreview = e.target?.result as string;
        });
      };
      reader.readAsDataURL(file);
    } else {
      this.imagePreview = null;
    }
  }

  // Add multiple files and upload immediately
  async addFiles(files: File[]) {
    if (!this.selectedDevice || files.length === 0) {
      return;
    }

    // Filter out files that exceed size limit
    const validFiles = files.filter(file => {
      const estimatedBase64Size = file.size * 1.37;
      return estimatedBase64Size <= API_CONFIG.MAX_IMAGE_SIZE;
    });

    if (validFiles.length === 0) {
      this.setError('No valid files to upload');
      return;
    }

    const startIndex = this.selectedFiles.length;
    
    runInAction(() => {
      // Add to selectedFiles array
      this.selectedFiles = [...this.selectedFiles, ...validFiles];
      this.isUploadingFiles = true;
      
      // Initialize upload status for new files
      validFiles.forEach((_, index) => {
        const fileIndex = startIndex + index;
        this.fileUploadStatus.set(fileIndex, 'uploading');
      });
      
      // Generate previews for new files
      validFiles.forEach((file, index) => {
        const fileIndex = startIndex + index;
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            runInAction(() => {
              this.filePreviews.set(fileIndex, e.target?.result as string);
            });
          };
          reader.readAsDataURL(file);
        }
      });

      // Clear text if files are selected
      if (validFiles.length > 0 && this.messageText.trim()) {
        this.clearTextInput();
      }
    });

    // Upload files one by one sequentially
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const fileIndex = startIndex + i;
      
      try {
        // Convert file to base64
        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.includes(',') ? result.split(',')[1] : result;
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Extract extension
        let extension = '';
        if (file.name && file.name.includes('.')) {
          extension = file.name.split('.').pop() || '';
        } else if (file.type) {
          const mimeParts = file.type.split('/');
          if (mimeParts.length === 2) {
            extension = mimeParts[1];
            if (extension === 'jpeg') extension = 'jpg';
          }
        }

        const fileData = {
          data: base64Data,
          type: file.type || 'application/octet-stream',
          extension: extension,
          name: file.name || `file.${extension}`
        };

        // Upload single file to session
        const response = await apiClient.sendContent({
          type: 'files',
          files: [fileData],
          targetDeviceId: this.selectedDevice.id,
          sessionId: this.currentSessionId || undefined
        }) as any;

        if (response && response.sessionId) {
          runInAction(() => {
            // Update session info
            this.currentSessionId = response.sessionId;
            this.sessionFiles = response.files || [];
            this.sessionTotalSize = response.totalSize || 0;
            // Mark file as uploaded
            this.fileUploadStatus.set(fileIndex, 'uploaded');
          });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err: any) {
        const errorMsg = err.message || 'Failed to upload file';
        runInAction(() => {
          // Mark file as error
          this.fileUploadStatus.set(fileIndex, 'error');
          this.error = errorMsg;
        });
        
        // If session expired, clear it
        if (errorMsg.includes('Session expired') || errorMsg.includes('Session not found')) {
          this.clearSession();
        }
        
        // Stop uploading remaining files on error
        break;
      }
    }

    // Check if all files are done uploading
    runInAction(() => {
      const allDone = Array.from(this.fileUploadStatus.values()).every(
        status => status === 'uploaded' || status === 'error'
      );
      if (allDone) {
        this.isUploadingFiles = false;
      }
    });
  }

  // Remove a file by index
  async removeFile(index: number) {
    const fileToRemove = this.selectedFiles[index];
    const uploadStatus = this.fileUploadStatus.get(index);
    
    // If file was uploaded, remove it from session
    if (this.currentSessionId && uploadStatus === 'uploaded') {
      // Find the file in sessionFiles that corresponds to this selected file
      // Since files are uploaded sequentially, we need to count only uploaded files
      // to find the correct index in sessionFiles
      let uploadedFileIndex = 0;
      for (let i = 0; i < index; i++) {
        if (this.fileUploadStatus.get(i) === 'uploaded') {
          uploadedFileIndex++;
        }
      }
      
      // Now uploadedFileIndex points to the correct file in sessionFiles
      if (uploadedFileIndex < this.sessionFiles.length) {
        const fileInfo = this.sessionFiles[uploadedFileIndex];
        if (fileInfo && fileInfo.url) {
          try {
            await this.removeFilesFromSession([fileInfo.url]);
            // removeFilesFromSession already updates sessionFiles and sessionTotalSize
          } catch (err) {
            console.error('Failed to remove file from session:', err);
            // Continue with local removal even if session removal fails
          }
        }
      }
    }

    runInAction(() => {
      this.selectedFiles = this.selectedFiles.filter((_, i) => i !== index);
      // Rebuild preview map with correct indices
      const newPreviews = new Map<number, string>();
      const newUploadStatus = new Map<number, 'uploading' | 'uploaded' | 'error'>();
      
      this.selectedFiles.forEach((file, i) => {
        // Find the original preview index (before removal)
        const originalIndex = i >= index ? i + 1 : i;
        if (this.filePreviews.has(originalIndex)) {
          newPreviews.set(i, this.filePreviews.get(originalIndex)!);
        }
        if (this.fileUploadStatus.has(originalIndex)) {
          newUploadStatus.set(i, this.fileUploadStatus.get(originalIndex)!);
        }
      });
      
      this.filePreviews = newPreviews;
      this.fileUploadStatus = newUploadStatus;
      
      // If no files left, clear session
      if (this.selectedFiles.length === 0) {
        this.clearSession();
      }
    });
  }

  clearTextInput() {
    this.messageText = '';
  }

  clearImageInput() {
    this.selectedImage = null;
    this.imagePreview = null;
  }

  clearFileInput() {
    this.selectedImage = null;
    this.imagePreview = null;
    this.selectedFiles = [];
    this.filePreviews.clear();
    this.fileUploadStatus.clear();
    this.isUploadingFiles = false;
  }

  clearInputs() {
    this.clearTextInput();
    this.clearFileInput();
  }

  // Toast Management
  showToast(message: string) {
    this.toast = { show: true, message };
  }

  hideToast() {
    this.toast = { show: false, message: '' };
  }

  // Message Deletion Timer Dialog Management
  showDeletionTimer() {
    this.showDeletionTimerDialog = true;
  }

  hideDeletionTimer() {
    this.showDeletionTimerDialog = false;
  }

  // Error Management
  setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  // Loading States
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  // Notification Management
  async requestNotificationPermission(): Promise<boolean> {
    return await notificationService.requestPermission();
  }

  isNotificationSupported(): boolean {
    return notificationService.isNotificationSupported();
  }

  getNotificationPermission(): NotificationPermission {
    return notificationService.getPermission();
  }

  // Computed Properties
  get currentDevice(): Device | null {
    const currentDeviceId = localStorage.getItem('web_device_id');
    return this.devices.find(device => device.id === currentDeviceId) || null;
  }

  get otherDevices(): Device[] {
    const currentDeviceId = localStorage.getItem('web_device_id');
    return this.devices.filter(device => device.id !== currentDeviceId);
  }

  get canSendMessage(): boolean {
    // Can't send if files are uploading
    if (this.isUploadingFiles) {
      return false;
    }
    return !!(this.messageText.trim() || this.selectedImage || this.selectedFiles.length > 0);
  }

  get totalSelectedFilesSize(): number {
    return this.selectedFiles.reduce((total, file) => total + file.size, 0);
  }

  get shouldShowMessageInput(): boolean {
    // Show message input if selected device is not the current device
    const currentDeviceId = localStorage.getItem('web_device_id');
    return this.selectedDevice?.id !== currentDeviceId;
  }

  get formatFileSize() {
    return (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
  }
}

// Create singleton instance
export const appStore = new AppStore();
